import Image from 'next/image';
import React, { forwardRef, Ref, useCallback, useMemo, useRef } from 'react';
import { DropDownFilterableStyle } from '../../../styles/components/common/ui';
import FormLayout from '../layout/FormLayout';

export interface IDropDownFilterableOption {
  value: number;
  label: string;
}

export interface IDropDownFilterableProps {
  options: IDropDownFilterableOption[];
  onChange: (event: number) => void;
  label?: string;
  name?: string;
  labelLayout?: 'horizontal' | 'vertical';
  value?: string;
  id?: string;
  disabled?: boolean;
  error?: string;
  defaultValue?: string | number | undefined;
  placheholder?: string;
  style?: React.CSSProperties;
}

const DropDownFilterable = forwardRef(
  (props: IDropDownFilterableProps, ref: Ref<any>) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchText, setSearchText] = React.useState('');
    const searchInputRef = useRef<HTMLInputElement | null>(null);

    const getCSSClasses = useCallback(() => {
      let cssClasses = DropDownFilterableStyle['dropdown-filterable'];
      if (isOpen) {
        cssClasses += ' ' + DropDownFilterableStyle['is-active'];
      }
      return cssClasses;
    }, [isOpen]);

    const restorePanelScroll = useCallback(() => {
      const panel = document.querySelector(
        `.${DropDownFilterableStyle['dropdown-filterable-panel']}`
      );
      if (panel) {
        panel.scrollTop = 0;
      }
    }, []);

    const onToggle = useCallback(() => {
      if (!props.disabled) {
        setIsOpen((prev) => {
          if (!prev) {
            searchInputRef.current?.focus();
          } else {
            searchInputRef.current?.blur();
          }
          return !prev;
        });

        setSearchText('');
        restorePanelScroll();
      }
    }, [props.disabled, restorePanelScroll]);

    const onBlur = useCallback(() => {
      if (!props.disabled) {
        setTimeout(() => {
          setSearchText('');
          restorePanelScroll();
          setIsOpen(false);
        }, 250);
      }
    }, [props.disabled, restorePanelScroll]);

    const onInputChange = useCallback(
      (ev: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(ev.target.value);
      },
      []
    );

    const onChange = useCallback(
      (value: number) => {
        props.onChange(value);
        setSearchText('');
        restorePanelScroll();
      },
      [props, restorePanelScroll]
    );

    const optionsFilteredByText = useMemo(() => {
      return props.options?.filter((option) =>
        option.label.toLowerCase().includes(searchText.toLowerCase())
      );
    }, [props.options, searchText]);

    return (
      <FormLayout label={props.label} error={props.error}>
        <div
          ref={ref}
          style={
            props.error
              ? {
                  ...props.style,
                  margin: '10px 0px 0px',
                  backgroundColor: '#FEF1F2',
                  borderColor: '#E18E96',
                }
              : { ...props.style }
          }
          className={getCSSClasses()}
          onBlur={onBlur}>
          <input
            ref={searchInputRef}
            disabled={props.disabled}
            type="text"
            value={searchText}
            placeholder={props.placheholder || ''}
            onClick={onToggle}
            onChange={onInputChange}
          />
          <Image
            quality={90}
            src="/assets/images/icons/NavBarItemChevronIcon.svg"
            priority={false}
            alt="Services"
            width={12}
            height={8}
            onClick={onToggle}
          />
          <div
            className={`${
              DropDownFilterableStyle['dropdown-filterable-panel']
            } ${
              isOpen
                ? DropDownFilterableStyle['dropdown-filterable-panel-is-active']
                : ''
            }`}>
            <ul>
              {optionsFilteredByText?.map((option) => (
                <li key={option.value} onClick={() => onChange(option.value)}>
                  {option.label}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </FormLayout>
    );
  }
);

DropDownFilterable.displayName = 'DropDownFilterable';
export default DropDownFilterable;
